import React, { useMemo, useState } from 'react';
import Modal from '../../../Components/Modal';
import HsCodeTree, { useHsTree, buildCodeLabelMap } from '../../../Components/HsCodeTree';

/* Commodity filter — hierarchical HS2/HS4 picker in a modal.
 * value is a flat array of mixed-length codes (2 = HS2 chapter, 4 = HS4
 * heading). quickLists (optional) renders org favourite lists as one-click
 * fills that merge into the current selection. */

const CommodityFilter = ({ value, onChange, placeholder, variant, disabledValues, quickLists }) => {
    const tree = useHsTree();
    const [open, setOpen] = useState(false);

    const labelMap = useMemo(() => buildCodeLabelMap(tree), [tree]);

    const selected = value || [];

    const remove = (v) => onChange(selected.filter((x) => x !== v));

    const applyList = (list) => {
        const blocked = new Set(disabledValues || []);
        const merged = new Set(selected);
        (list.codes || []).forEach((c) => {
            if (!blocked.has(c)) merged.add(c);
        });
        onChange([...merged]);
    };

    const chipClass = variant === 'accent' ? 'tag-chip accent' : 'tag-chip muted';

    return (
        <>
            {selected.length > 0 && (
                <div className="tags" style={{ marginBottom: 6 }}>
                    {selected.map((v) => {
                        const name = labelMap.get(v) ?? '';
                        return (
                            <span key={v} className={chipClass}>
                                <span className="commodity-pill-text">
                                    {v}{name ? ` · ${name}` : ''}
                                </span>
                                <button
                                    type="button"
                                    aria-label="Remove"
                                    onClick={() => remove(v)}
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            <button
                type="button"
                className="btn btn-secondary btn-block commodity-picker-btn"
                onClick={() => setOpen(true)}
            >
                {selected.length === 0
                    ? (placeholder || 'Choose…')
                    : `${selected.length} selected — edit`}
            </button>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={placeholder || 'Choose commodities'}
                width="640px"
            >
                <div className="commodity-picker-modal">
                    {quickLists && quickLists.length > 0 && (
                        <div className="hs-quicklists">
                            <span className="hs-quicklists-label">My lists</span>
                            {quickLists.map((list) => (
                                <button
                                    key={list.id}
                                    type="button"
                                    className="hs-quicklist-btn"
                                    title={`Add ${list.codes.length} code${list.codes.length === 1 ? '' : 's'} from "${list.name}"`}
                                    onClick={() => applyList(list)}
                                >
                                    {list.name}
                                    <span className="hs-quicklist-count">{list.codes.length}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <HsCodeTree
                        value={selected}
                        onChange={onChange}
                        disabledValues={disabledValues}
                    />
                </div>
            </Modal>
        </>
    );
};

export default CommodityFilter;
